import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Group, Modal, SegmentedControl, SimpleGrid, Stack, Table, Tabs, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Timestamp, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { EtfData, EtfInav, Fund, GtnInavTest, PositionDetails } from '../../types';
import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import useFirestoreDoc from '../../helpers/useFirestoreDoc';
import { EtfContext, EtfContextValue, isEtfSectionError, kuwaitDateTimeString, navDateOf } from './etf_common';
import EtfOperations from './etf_operations';
import EtfHoldings from './etf_holdings';
import EtfPcf from './etf_pcf';
import EtfLogs from './etf_logs';
import EtfIdentifiers from './etf_identifiers';
import EtfRequests from './etf_requests';

//iNAV freshness: GTN pushes every ~15s, so green within 15s, yellow within a minute, gray after that
const inavColor = (ageSeconds: number | null) => (ageSeconds == null || ageSeconds > 60 ? 'gray' : ageSeconds > 15 ? 'yellow' : 'green');
const ago = (ageSeconds: number | null) => (ageSeconds == null ? 'never' : ageSeconds < 60 ? `${ageSeconds}s ago` : `${Math.floor(ageSeconds / 60)}m ago`);
const kuwaitTime = (t: string | number) => new Date(t).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kuwait' });

//re-renders every second so ages, colours and countdowns move without new data
const useNow = () => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);
    return now;
};

//card border follows the age of the last push
const EtfInavCard = ({ fundId }: { fundId: string }) => {
    const db = useFirestore();
    const { data: inav } = useFirestoreDoc<EtfInav>(doc(db, 'funds', fundId, 'private', 'inav'));
    const now = useNow();

    const age = (t: Timestamp | undefined) => (t ? Math.max(0, Math.round((now - t.toMillis()) / 1000)) : null);
    const receivedAge = age(inav?.receivedAt);
    const pulledAge = age(inav?.pulledAt);
    const color = inavColor(receivedAge);

    return (
        <Card withBorder padding="sm" miw={180} style={{ borderColor: `var(--mantine-color-${color}-6)`, borderWidth: 2 }}>
            <Text size="xs" c="dimmed">iNAV</Text>
            <Text fw={700} size="lg" c={color === 'gray' ? 'dimmed' : undefined}>{inav ? inav.inav.toFixed(4) : '—'}</Text>
            <Text size="xs" c="dimmed">received {ago(receivedAge)} · {kuwaitDateTimeString(inav?.receivedAt?.toDate())}</Text>
            <Text size="xs" c="dimmed">pulled by {inav?.pulledBy ?? 'nobody'} {ago(pulledAge)}</Text>
        </Card>
    );
};

const GTN_TEST_DURATIONS = [{ label: '30s', value: '30' }, { label: '1m', value: '60' }, { label: '3m', value: '180' }];
const GTN_TEST_STATUS: Record<GtnInavTest['status'], { color: string; label: string }> = {
    connecting: { color: 'yellow', label: 'Connecting' },
    live: { color: 'green', label: 'Live' },
    done: { color: 'gray', label: 'Finished' },
    error: { color: 'red', label: 'Error' },
};

//GTN QA feed demo: testGtnInav holds one WebSocket session and mirrors it to etf_inav_test/current, this listens.
//Shows whoever's run is current, so a second viewer sees the same test live.
const GtnInavTestPanel = () => {
    const db = useFirestore();
    const functions = useFunctions();
    const now = useNow();
    const { data: run } = useFirestoreDoc<GtnInavTest>(doc(db, 'etf_inav_test', 'current'));
    const [seconds, setSeconds] = useState('60');
    const [starting, setStarting] = useState(false);
    const [callError, setCallError] = useState<string | null>(null);

    const running = !!run && (run.status === 'connecting' || run.status === 'live') && now < run.endsAt;
    const since = (t?: number) => (t ? Math.max(0, Math.round((now - t) / 1000)) : null);

    //the callable returns when the run ends; progress arrives through the doc meanwhile
    const start = () => {
        setStarting(true);
        setCallError(null);
        httpsCallable(functions, 'testGtnInav', { timeout: (Number(seconds) + 90) * 1000 })({ seconds: Number(seconds) })
            .catch((e) => setCallError(e instanceof Error ? e.message : 'Test failed'))
            .finally(() => setStarting(false));
    };

    return (
        <Stack>
            <Group justify="space-between">
                <SegmentedControl data={GTN_TEST_DURATIONS} value={seconds} onChange={setSeconds} disabled={running || starting} />
                <Button onClick={start} loading={starting} disabled={running}>Start</Button>
            </Group>
            {callError && callError !== run?.error && (
                <Alert color="red" icon={<IconAlertTriangle />}>{callError}</Alert>
            )}
            {run && (
                <>
                    <Group justify="space-between">
                        <Badge size="lg" color={GTN_TEST_STATUS[run.status].color}>{GTN_TEST_STATUS[run.status].label}</Badge>
                        <Text size="xs" c="dimmed">
                            {running ? `${Math.max(0, Math.ceil((run.endsAt - now) / 1000))}s left` : `${kuwaitDateTimeString(new Date(run.startedAt))}${run.closed ? ` · ${run.closed}` : ''}`}
                            {` · started by ${run.startedBy}`}
                        </Text>
                    </Group>
                    <div>
                        <Text size="xs" c="dimmed">iNAV · {run.symbol}</Text>
                        <Text fw={700} fz={36}>{run.inav != null ? run.inav.toFixed(4) : '—'}</Text>
                        <Text size="xs" c="dimmed">
                            GTN time {kuwaitDateTimeString(run.ludt ? new Date(run.ludt) : null)} · received {ago(since(run.receivedAt))}
                        </Text>
                    </div>
                    <SimpleGrid cols={3}>
                        <div>
                            <Text size="xs" c="dimmed">Updates</Text>
                            <Text fw={700}>{run.updates}</Text>
                        </div>
                        <div>
                            <Text size="xs" c="dimmed">Heartbeats</Text>
                            <Text fw={700}>{run.heartbeats} <Text span size="xs" c="dimmed">last {ago(since(run.lastHeartbeatAt))}</Text></Text>
                        </div>
                        <div>
                            <Text size="xs" c="dimmed">Entitled exchanges</Text>
                            <Text fw={700}>{run.entitlements?.join(', ') || '—'}</Text>
                        </div>
                    </SimpleGrid>
                    {run.error && (
                        <Alert color="red" icon={<IconAlertTriangle />}>{run.error}</Alert>
                    )}
                    {run.ticks.length > 0 && (
                        <Table
                            striped
                            withTableBorder
                            data={{
                                head: ['iNAV', 'GTN time (Kuwait)', 'Received'],
                                body: run.ticks.slice(0, 10).map((t) => [t.p.toFixed(4), kuwaitTime(t.ludt), kuwaitTime(t.receivedAt)]),
                            }}
                        />
                    )}
                </>
            )}
            <Text size="xs" c="dimmed">GTN QA test symbol {run?.symbol ?? 'KSEETF~BKTESTETF1'} — not a real fund price.</Text>
        </Stack>
    );
};

const GtnInavTestButton = () => {
    const [opened, setOpened] = useState(false);
    return (
        <>
            <Button size="xs" variant="light" onClick={() => setOpened(true)}>GTN live iNAV test</Button>
            <Modal opened={opened} onClose={() => setOpened(false)} title="GTN iNAV feed — live test" size="lg">
                <GtnInavTestPanel />
            </Modal>
        </>
    );
};

//one ETF: listens to funds/{id}/private/nav_position (the latest NAV date's position, saved on every NAV sync and by
//Refresh Data) and rebuilds the PCF (getEtfData) whenever it changes.
const EtfFund = ({ fund }: { fund: Fund }) => {
    const db = useFirestore();
    const functions = useFunctions();

    const { data: position, loading: positionLoading } = useFirestoreDoc<PositionDetails>(doc(db, 'funds', fund.id, 'private', 'nav_position'));
    const positionUpdatedAt = position?.updated_at?.toMillis() ?? null;

    const [data, setData] = useState<EtfData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    //rebuild whenever the NAV position changes (initial load, NAV sync, or a manual refresh)
    useEffect(() => {
        if (positionUpdatedAt == null) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        const getEtfData = httpsCallable<{ fundId: string }, EtfData>(functions, 'getEtfData');
        getEtfData({ fundId: fund.id })
            .then((result) => { if (!cancelled) setData(result.data); })
            .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to build ETF data'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [fund.id, functions, positionUpdatedAt]);

    const holdings = data && !isEtfSectionError(data.holdings) ? data.holdings : null;
    const stats: { label: string; value: string }[] = [
        { label: 'NAV / Unit', value: fund.nav_per_unit != null ? fund.nav_per_unit.toFixed(4) : '—' },
        { label: 'NAV Date', value: navDateOf(fund) ?? '—' },
        { label: 'Issued Units', value: fund.issued_units != null ? fund.issued_units.toLocaleString() : '—' },
        { label: 'Basket Size', value: fund.basket_size != null ? fund.basket_size.toLocaleString() : 'NOT SET' },
        { label: 'Cut-off (Kuwait)', value: fund.etf_cutoff ?? 'NOT SET' },
        { label: 'Position As Of', value: holdings?.asOf ?? '—' },
        { label: 'Position Updated', value: kuwaitDateTimeString(position?.updated_at?.toDate()) },
    ];

    const context: EtfContext = { fund, position: position ?? null, data, loading, error };

    return (
        <EtfContextValue.Provider value={context}>
            <Group justify="space-between" mb="md">
                <SimpleGrid cols={{ base: 2, sm: 4, md: 7 }} style={{ flex: 1 }}>
                    {stats.map((stat) => (
                        <div key={stat.label}>
                            <Text size="xs" c="dimmed">{stat.label}</Text>
                            <Text fw={700}>{stat.value}</Text>
                        </div>
                    ))}
                </SimpleGrid>
                <Stack gap={4}>
                    <EtfInavCard fundId={fund.id} />
                    <GtnInavTestButton />
                </Stack>
            </Group>
            {!positionLoading && !position && (
                <Alert color="orange" icon={<IconAlertTriangle />} title="No position synced yet" mb="md">
                    <code>funds/{fund.id}/private/nav_position</code> does not exist. It is saved when the NAV is synced — or click Refresh Data on the Operations tab to pull the latest NAV date's position.
                </Alert>
            )}
            {error && (
                <Alert color="red" icon={<IconAlertTriangle />} title="Failed to build ETF data" mb="md">
                    <Text>{error}</Text>
                </Alert>
            )}
            <Tabs defaultValue="operations" keepMounted={false}>
                <Tabs.List mb="md">
                    <Tabs.Tab value="operations">Operations</Tabs.Tab>
                    <Tabs.Tab value="requests">Requests</Tabs.Tab>
                    <Tabs.Tab value="holdings">Holdings</Tabs.Tab>
                    <Tabs.Tab value="pcf">PCF / Basket</Tabs.Tab>
                    <Tabs.Tab value="logs">Logs</Tabs.Tab>
                    <Tabs.Tab value="identifiers">Identifiers</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="operations"><EtfOperations /></Tabs.Panel>
                <Tabs.Panel value="requests"><EtfRequests /></Tabs.Panel>
                <Tabs.Panel value="holdings"><EtfHoldings /></Tabs.Panel>
                <Tabs.Panel value="pcf"><EtfPcf /></Tabs.Panel>
                <Tabs.Panel value="logs"><EtfLogs /></Tabs.Panel>
                <Tabs.Panel value="identifiers"><EtfIdentifiers /></Tabs.Panel>
            </Tabs>
        </EtfContextValue.Provider>
    );
};

export default EtfFund;
